import { generateFiltersPath } from '../utils/history';
import { preparePageQuery } from '../utils/queryParams';
import { buildTitle } from '../utils/seo';

const STRUCTURED_DATA = {
  uniplaces: {
    homepage: () => [
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        name: 'Uniplaces',
        legalName: 'Universityplaces - Serviços de Internet, Lda.',
        url: process.env.APP_URL,
        foundingDate: '2013',
        logo: `${process.env.APP_URL}static/images/logo.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Largo Duque do Cadaval N17 Fração I',
          addressLocality: 'Lisbon',
          postalCode: '1200-160',
          addressCountry: 'Portugal'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@uniplaces.com'
        },
        sameAs: [
          'https://facebook.com/uniplaces',
          'https://instagram.com/uniplaces',
          'https://twitter.com/uniplaces',
          'https://youtube.com/user/uniplacesvideos',
          'https://pinterest.com/uniplaces',
          'https://linkedin.com/company/uniplaces',
          'https://blog.uniplaces.com'
        ]
      },
      {
        '@context': 'http://schema.org',
        '@type': 'WebSite',
        alternateName: 'Uniplaces.com',
        name: 'Uniplaces',
        url: `${process.env.APP_URL}`,
        potentialAction: {
          '@type': 'SearchAction',
          'query-input': 'required name=search_term_string',
          target: `${process.env.APP_URL}accommodation/{search_term_string}`
        }
      }
    ],
    search: ({
      t,
      title,
      description,
      city,
      cityTranslation,
      latLng,
      count,
      lower,
      higher,
      currency,
      translation,
      locale,
      query = {},
      neighbourhood = '',
      university = ''
    }) => [
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        name: 'Uniplaces',
        legalName: 'Universityplaces - Serviços de Internet, Lda.',
        url: process.env.APP_URL,
        foundingDate: '2013',
        logo: `${process.env.APP_URL}static/images/logo.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Largo Duque do Cadaval N17 Fração I',
          addressLocality: 'Lisbon',
          postalCode: '1200-160',
          addressCountry: 'Portugal'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@uniplaces.com'
        },
        sameAs: [
          'https://facebook.com/uniplaces',
          'https://instagram.com/uniplaces',
          'https://twitter.com/uniplaces',
          'https://youtube.com/user/uniplacesvideos',
          'https://pinterest.com/uniplaces',
          'https://linkedin.com/company/uniplaces',
          'https://blog.uniplaces.com'
        ]
      },
      {
        '@context': 'http://schema.org',
        '@type': ['Apartment', 'Product'],
        name: `${title}`,
        description: `${description}`,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: currency,
          offerCount: count,
          lowPrice: lower,
          highPrice: higher
        },
        hasMap: `http://www.google.com/maps/place/${latLng}`
      },
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          ...getBreadcrumbItems(city, cityTranslation, 'accommodation', query, locale, t),
          ...getBreadcrumbItemsByType(
            'neighbourhood',
            neighbourhood,
            city,
            cityTranslation,
            translation,
            'accommodation',
            query,
            locale,
            t
          ),
          ...getBreadcrumbItemsByType(
            'university',
            university,
            city,
            cityTranslation,
            translation,
            'accommodation',
            query,
            locale,
            t
          )
        ]
      }
    ],
    offer: model => [
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        name: 'Uniplaces',
        legalName: 'Universityplaces - Serviços de Internet, Lda.',
        url: process.env.APP_URL,
        foundingDate: '2013',
        logo: `${process.env.APP_URL}static/images/logo.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Largo Duque do Cadaval N17 Fração I',
          addressLocality: 'Lisbon',
          postalCode: '1200-160',
          addressCountry: 'Portugal'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@uniplaces.com'
        },
        sameAs: [
          'https://facebook.com/uniplaces',
          'https://instagram.com/uniplaces',
          'https://twitter.com/uniplaces',
          'https://youtube.com/user/uniplacesvideos',
          'https://pinterest.com/uniplaces',
          'https://linkedin.com/company/uniplaces',
          'https://blog.uniplaces.com'
        ]
      },
      {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: model.title,
        description: model.description,
        image: model.mainPhoto,
        brand: {
          '@type': 'RealEstateAgent',
          name: 'Uniplaces',
          url: process.env.APP_URL
        },
        offers: {
          '@type': 'Offer',
          availability: 'OnlineOnly',
          url: model.completeUrl,
          price: model.price,
          priceCurrency: model.priceCurrency
        },
        ...(model.reviewCount
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: model.ratingValue,
                bestRating: model.bestRating,
                reviewCount: model.reviewCount
              }
            }
          : {})
      },
      {
        '@context': 'http://schema.org',
        '@type': 'Accommodation',
        name: model.title,
        description: model.description,
        numberOfRooms: model.numberOfRooms,
        photo: model.mainPhoto,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: `${model.latitude}`,
          longitude: `${model.longitude}`
        },
        potentialAction: {
          '@type': 'ReserveAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: model.completeUrl,
            actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
          },
          result: {
            '@type': 'LodgingReservation',
            name: 'Rent Now'
          }
        }
      },
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': model.homeBreadCrumb.url,
              name: model.homeBreadCrumb.name
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': model.cityBreadCrumb.url,
              name: model.cityBreadCrumb.name
            }
          },
          ...(model.neighborhoodBreadCrumb
            ? [
                {
                  '@type': 'ListItem',
                  position: 3,
                  item: {
                    '@id': model.neighborhoodBreadCrumb.url,
                    name: model.neighborhoodBreadCrumb.name
                  }
                },
                {
                  '@type': 'ListItem',
                  position: 4,
                  item: {
                    '@id': model.offerBreadCrumb.url,
                    name: model.offerBreadCrumb.name
                  }
                }
              ]
            : [
                {
                  '@type': 'ListItem',
                  position: 3,
                  item: {
                    '@id': model.offerBreadCrumb.url,
                    name: model.offerBreadCrumb.name
                  }
                }
              ])
        ]
      }
    ]
  },
  uniresidences: {
    homepage: () => [
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        name: 'Uniresidences',
        legalName: 'Universityplaces - Serviços de Internet, Lda.',
        url: `${process.env.APP_URL}residences`,
        foundingDate: '2013',
        logo: `${process.env.APP_URL}residences/static/images/logo.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Largo Duque do Cadaval N17 Fração I',
          addressLocality: 'Lisbon',
          postalCode: '1200-160',
          addressCountry: 'Portugal'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@uniplaces.com'
        },
        sameAs: [
          'https://www.uniresidences.com',
          'https://facebook.com/uniresidences',
          'https://instagram.com/uniresidences_',
          'https://twitter.com/uniresidences_',
          'https://medium.com/uniresidences',
          'https://linkedin.com/company/uniresidences'
        ]
      },
      {
        '@context': 'http://schema.org',
        '@type': 'WebSite',
        alternateName: 'Uniresidences.com',
        name: 'Uniresidences',
        url: `${process.env.APP_URL}residences`,
        potentialAction: {
          '@type': 'SearchAction',
          'query-input': 'required name=search_term_string',
          target: `${process.env.APP_URL}residences/{search_term_string}`
        }
      }
    ],
    search: ({
      t,
      title,
      description,
      city,
      cityTranslation,
      latLng,
      count,
      lower,
      higher,
      currency,
      translation,
      locale,
      query = {},
      neighbourhood = '',
      university = ''
    }) => [
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        name: 'Uniresidences',
        legalName: 'Universityplaces - Serviços de Internet, Lda.',
        url: `${process.env.APP_URL}residences`,
        foundingDate: '2013',
        logo: `${process.env.APP_URL}residences/static/images/logo.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Largo Duque do Cadaval N17 Fração I',
          addressLocality: 'Lisbon',
          postalCode: '1200-160',
          addressCountry: 'Portugal'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@uniplaces.com'
        },
        sameAs: [
          'https://www.uniresidences.com',
          'https://facebook.com/uniresidences',
          'https://instagram.com/uniresidences_',
          'https://twitter.com/uniresidences_',
          'https://medium.com/uniresidences',
          'https://linkedin.com/company/uniresidences'
        ]
      },
      {
        '@context': 'http://schema.org',
        '@type': ['Apartment', 'Product'],
        name: `${title}`,
        description: `${description}`,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: currency,
          offerCount: count,
          lowPrice: lower,
          highPrice: higher
        },
        hasMap: `http://www.google.com/maps/place/${latLng}`
      },
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          ...getBreadcrumbItems(city, cityTranslation, 'residences', query, locale, t),
          ...getBreadcrumbItemsByType(
            'neighbourhood',
            neighbourhood,
            city,
            cityTranslation,
            translation,
            'residences',
            query,
            locale,
            t
          ),
          ...getBreadcrumbItemsByType(
            'university',
            university,
            city,
            cityTranslation,
            translation,
            'residences',
            query,
            locale,
            t
          )
        ]
      }
    ]
  }
};

const getBreadcrumbItems = (city, cityTranslation, searchDomain, query, locale, t) => {
  const isResidence = searchDomain === 'residences';

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@id': isResidence ? `${process.env.APP_URL}${searchDomain}` : process.env.APP_URL,
        name: 'Uniplaces'
      }
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@id': `${process.env.APP_URL}${searchDomain}/${city}`,
        name: buildTitle({ t, language: locale, detailedLocation: cityTranslation, searchQuery: {} })
      }
    }
  ];

  const breadcrumbData = { city, searchDomain, query, locale, cityTranslation, t };
  OPTIONAL_BREADCRUMB_ITEMS.forEach(
    ({ verify, create }) => verify(breadcrumbData, items) && items.push(create(breadcrumbData, items))
  );

  return items;
};

const getBreadcrumbItemsByType = (type, name, city, cityTranslation, translation, searchDomain, query, locale, t) => {
  if (!name) {
    return [];
  }

  const detailedLocation = `${translation} ${cityTranslation}`;

  const items = [
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@id': `${process.env.APP_URL}${searchDomain}/${city}/${type}/${name}`,
        name: buildTitle({ t, language: locale, detailedLocation, searchQuery: {} })
      }
    }
  ];

  const breadcrumbData = { type, name, city, searchDomain, query, locale, detailedLocation, t };
  NEIGHBOURHOOD_AND_UNIVERSITY_OPTIONAL_BREADCRUMB_ITEMS_BY_TYPE.forEach(
    ({ verify, create }) => verify(breadcrumbData, items) && items.push(create(breadcrumbData, items))
  );

  return items;
};

const getRentAndHousingFilters = query => ({
  'rent-type': query?.['rent-type'],
  'housing-type': query?.['housing-type']
});

const checkIfHasRentOrHousingFilters = query => {
  const rentAndHousingFilters = getRentAndHousingFilters(query);
  return rentAndHousingFilters['rent-type'] || rentAndHousingFilters['housing-type'];
};

const checkIfHasAnyFilters = (query, locale) => {
  const filtersPathWithoutRentAndHousingType = generateFiltersPath(
    { ...query, 'rent-type': null, 'housing-type': null },
    locale
  );
  return !!`${filtersPathWithoutRentAndHousingType}${preparePageQuery(query)}`;
};

const OPTIONAL_BREADCRUMB_ITEMS = [
  {
    verify({ query }) {
      return checkIfHasRentOrHousingFilters(query);
    },
    create({ city, searchDomain, query, locale, cityTranslation, t }) {
      const rentAndHousingFilters = getRentAndHousingFilters(query);

      return {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@id': `${process.env.APP_URL}${searchDomain}/${city}/f/${generateFiltersPath(
            rentAndHousingFilters,
            locale
          )}`,
          name: buildTitle({
            t,
            language: locale,
            detailedLocation: cityTranslation,
            searchQuery: rentAndHousingFilters
          })
        }
      };
    }
  },
  {
    verify({ query, locale, cityTranslation, t }, currentItems) {
      const itemName = buildTitle({
        t,
        language: locale,
        detailedLocation: cityTranslation,
        searchQuery: query
      });
      const isNameDuplicated = currentItems.some(({ item }) => item.name === itemName);
      if (isNameDuplicated) {
        return false;
      }

      return checkIfHasAnyFilters(query, locale);
    },
    create({ city, searchDomain, query, locale, cityTranslation, t }) {
      const position = checkIfHasRentOrHousingFilters(query) ? 4 : 3;

      return {
        '@type': 'ListItem',
        position,
        item: {
          '@id': `${process.env.APP_URL}${searchDomain}/${city}/f/${generateFiltersPath(
            query,
            locale
          )}${preparePageQuery(query)}`,
          name: buildTitle({
            t,
            language: locale,
            detailedLocation: cityTranslation,
            searchQuery: query
          })
        }
      };
    }
  }
];

const NEIGHBOURHOOD_AND_UNIVERSITY_OPTIONAL_BREADCRUMB_ITEMS_BY_TYPE = [
  {
    verify({ query }) {
      return checkIfHasRentOrHousingFilters(query);
    },
    create({ type, name, city, searchDomain, query, locale, detailedLocation, t }) {
      const rentAndHousingFilters = getRentAndHousingFilters(query);

      return {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@id': `${process.env.APP_URL}${searchDomain}/${city}/${type}/${name}/f/${generateFiltersPath(
            rentAndHousingFilters,
            locale
          )}`,
          name: buildTitle({
            t,
            language: locale,
            detailedLocation,
            searchQuery: rentAndHousingFilters
          })
        }
      };
    }
  },
  {
    verify({ query, locale, detailedLocation, t }, currentItems) {
      const itemName = buildTitle({
        t,
        language: locale,
        detailedLocation,
        searchQuery: query
      });
      const isNameDuplicated = currentItems.some(({ item }) => item.name === itemName);
      if (isNameDuplicated) {
        return false;
      }

      return checkIfHasAnyFilters(query, locale);
    },
    create({ type, name, city, searchDomain, query, locale, detailedLocation, t }) {
      const position = checkIfHasRentOrHousingFilters(query) ? 5 : 4;

      return {
        '@type': 'ListItem',
        position,
        item: {
          '@id': `${process.env.APP_URL}${searchDomain}/${city}/${type}/${name}/f/${generateFiltersPath(
            query,
            locale
          )}${preparePageQuery(query)}`,
          name: buildTitle({
            t,
            language: locale,
            detailedLocation,
            searchQuery: query
          })
        }
      };
    }
  }
];

export default STRUCTURED_DATA;
